import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLang } from "../LangContext";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const COPY = {
  sv: {
    checking: "Kontrollerar betalningsstatus…",
    processing: "Betalningen behandlas…",
    success_title: "Betalt och bokat",
    success_body: "Din tid är bekräftad och kvitto är skickat till din e-post.",
    error_title: "Något gick fel",
    error_body:
      "Om beloppet drogs men du inte fick bekräftelse — kontakta oss så reder vi ut det.",
    expired: "Betalningssessionen har gått ut. Försök igen.",
    home: "Tillbaka till startsidan",
  },
  en: {
    checking: "Checking payment status…",
    processing: "Payment processing…",
    success_title: "Paid and booked",
    success_body:
      "Your appointment is confirmed and a receipt has been sent to your email.",
    error_title: "Something went wrong",
    error_body:
      "If you were charged but didn't receive a confirmation, contact us and we'll sort it out.",
    expired: "Payment session expired. Please try again.",
    home: "Back to homepage",
  },
};

export default function PaymentSuccess() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.sv;
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get("session_id");

  const [phase, setPhase] = useState("checking"); // checking | processing | success | expired | error
  const [data, setData] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setPhase("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data: d } = await axios.get(`${API}/payments/status/${sessionId}`);
        setData(d);
        if (d.payment_status === "paid" && d.booking_id) {
          setPhase("success");
          return;
        }
        if (d.status === "expired") {
          setPhase("expired");
          return;
        }
        if (attempts.current >= 8) {
          setPhase("processing");
          return;
        }
        setPhase(attempts.current < 3 ? "checking" : "processing");
        setTimeout(poll, 2000);
      } catch (_) {
        if (attempts.current < 6) {
          setTimeout(poll, 2500);
        } else {
          setPhase("error");
        }
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <main className="min-h-screen bg-cream text-ink py-24 px-6 flex items-center">
      <div className="max-w-xl mx-auto">
        <p className="eyebrow text-ink2 mb-4">— Stripe</p>
        {phase === "checking" || phase === "processing" ? (
          <div data-testid="pay-checking">
            <h1 className="headline text-4xl md:text-5xl mb-6">
              {phase === "checking" ? c.checking : c.processing}
            </h1>
            <div className="w-12 h-px bg-ink animate-pulse" />
          </div>
        ) : phase === "success" ? (
          <div data-testid="pay-success">
            <h1 className="headline text-4xl md:text-5xl mb-6">{c.success_title}</h1>
            <p className="text-base text-ink2 leading-relaxed mb-6">{c.success_body}</p>
            {data?.when_label && (
              <p className="font-serif text-2xl text-ink mb-2">{data.when_label}</p>
            )}
            {data?.amount && (
              <p className="text-sm text-ink2">
                {data.amount} {String(data.currency || "SEK").toUpperCase()} ·
                Stripe-session {sessionId?.slice(-8)}
              </p>
            )}
            <a
              href="/"
              data-testid="pay-home"
              className="mt-10 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-ink text-cream text-sm"
            >
              {c.home} →
            </a>
          </div>
        ) : phase === "expired" ? (
          <div data-testid="pay-expired">
            <h1 className="headline text-4xl md:text-5xl mb-6">{c.expired}</h1>
            <a href="/#contact" className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-ink text-cream text-sm">
              {c.home} →
            </a>
          </div>
        ) : (
          <div data-testid="pay-error">
            <h1 className="headline text-4xl md:text-5xl mb-6">{c.error_title}</h1>
            <p className="text-base text-ink2 leading-relaxed mb-8">{c.error_body}</p>
            <a href="/" className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-ink text-cream text-sm">
              {c.home} →
            </a>
          </div>
        )}
      </div>
    </main>
  );
}
